import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Snackbar, IconButton, Alert
} from "@mui/material";
import AppLoader from '../../../components/AppLoader';
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { fetchJson } from "../../../utils/fetchJson";

export default function ExpensesPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ description: "", amount: "", incurredAt: "", category: "", vendor: "" });
  const [snack, setSnack] = useState("");

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true); setError("");
    // Instant cache prefill scoped by school|phase|ay
    try {
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}|${ayId || '-'}`;
      const cacheKey = `expenses:list:${ctxKey}`;
      const cachedRaw = localStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (Array.isArray(cached)) setRows(cached);
      }
    } catch(_) {}

    let maxGuard; try { maxGuard = setTimeout(() => setLoading(false), 2000); } catch(_) {}
    try {
      const res = await fetchJson("/api/finance/expenses?page=1&pageSize=100", { method: 'GET' });
      const data = res.expenses || res.data || [];
      setRows(data);
      try {
        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const ctxKey = `${schoolId}|${phaseId || phaseCode || '-'}|${ayId || '-'}`;
        const cacheKey = `expenses:list:${ctxKey}`;
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch(_) {}
    } catch (e) { setError(e.message) }
    finally { setLoading(false); try { clearTimeout(maxGuard); } catch(_) {} }
  }

  function openCreate() {
    setCurrent(null);
    setForm({ description: "", amount: "", incurredAt: new Date().toISOString().split('T')[0], category: "", vendor: "" });
    setDialogOpen(true);
  }
  function openEdit(r) {
    setCurrent(r);
    setForm({ 
      description: r.description || "", 
      amount: r.amount || "", 
      incurredAt: (r.incurredAt || r.date || '').slice(0,10),
      category: r.category || "",
      vendor: r.vendor || ""
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    setError("");
    // Category is not optional however the form once looked: expenses.categoryId
    // is NOT NULL, so a blank one used to surface as a raw database error that
    // named no field. Checked here so the message points at the field at fault.
    if (!form.description || !form.amount || !form.incurredAt || !String(form.category || '').trim()) {
      setError(t('expensesPage.errors.requiredFields'));
      return;
    }
    try {
      const url = current ? `/api/finance/expenses/${current.id}` : `/api/finance/expenses`;
      const method = current ? "PUT" : "POST";
      await fetchJson(url, {
        method, 
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      setSnack(current ? t('expensesPage.snack.updated') : t('expensesPage.snack.created'));
      setDialogOpen(false);
      load();
    } catch (e) { setError(e.message) }
  }

  async function handleDelete(id) {
    if (!window.confirm(t('expensesPage.confirmDelete'))) return;
    try {
      await fetchJson(`/api/finance/expenses/${id}`, { method: "DELETE" });
      setSnack(t('expensesPage.snack.deleted'));
      load();
    } catch (e) { alert(e.message) }
  }

  const columns = [
    { field: "description", headerName: t('expensesPage.columns.description'), flex: 1 },
    { field: "amount", headerName: t('expensesPage.columns.amount'), width: 120, valueFormatter: (params) => new Intl.NumberFormat('en-CM', { style: 'currency', currency: 'XAF', minimumFractionDigits: 0 }).format(params.value || 0) },
    { field: "category", headerName: t('expensesPage.columns.category'), width: 140 },
    { field: "vendor", headerName: t('expensesPage.columns.vendor'), width: 140 },
    { field: "incurredAt", headerName: t('expensesPage.columns.date'), width: 120, valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : '' },
    {
      field: "actions",
      headerName: t('expensesPage.columns.actions'),
      width: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" onClick={() => openEdit(row)} startIcon={<EditIcon />}>{t('actions.edit')}</Button>
          <Button size="small" color="error" onClick={() => handleDelete(row.id)} startIcon={<DeleteIcon />}>{t('actions.delete')}</Button>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Typography variant="h4" gutterBottom>
        {t('expensesPage.title')}
      </Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ mb:2 }}>{t('expensesPage.newExpense')}</Button>
      {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}
      {loading
        ? <AppLoader variant="circular3" size={58} centered minHeight="40vh" messages={[t('expensesPage.loading'), t('expensesPage.applyingPhase'), t('expensesPage.applyingAY')]} />
        : <>
            {/* Added up from the rows on screen rather than fetched, so the
                figure cannot disagree with the list under it. These are the
                expenses of the SELECTED ACADEMIC YEAR: the server bounds them
                by date, since expenses carry no year of their own. */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2 }}>
              <Typography variant="h5">
                {new Intl.NumberFormat('en-CM', { style: 'currency', currency: 'XAF', minimumFractionDigits: 0 })
                  .format(rows.reduce((n, r) => n + Number(r.amount || 0), 0))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('expensesPage.totalCount', { count: rows.length, defaultValue: 'across {{count}} entries this year' })}
              </Typography>
            </Box>
            <div style={{ height: 500, width: "100%" }}>
              <DataGrid rows={rows} columns={columns} pageSize={10}/>
            </div>
          </>
      }

      <Dialog open={dialogOpen} onClose={()=>setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{current ? t('expensesPage.editExpense') : t('expensesPage.newExpense')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}
          <TextField
            label={t('expensesPage.fields.description')}
            fullWidth sx={{ mt:2 }}
            value={form.description}
            onChange={e=>setForm(f=>({...f,description:e.target.value}))}
            required
            multiline
            rows={2}
          />
          <TextField
            label={t('expensesPage.fields.amount')}
            type="number"
            fullWidth sx={{ mt:2 }}
            value={form.amount}
            onChange={e=>setForm(f=>({...f,amount:e.target.value}))}
            required
          />
          <TextField
            label={t('expensesPage.fields.category')}
            fullWidth sx={{ mt:2 }}
            value={form.category}
            onChange={e=>setForm(f=>({...f,category:e.target.value}))}
            placeholder={t('expensesPage.placeholders.category')}
            helperText={t('expensesPage.categoryHelp', { defaultValue: 'A new name is created the first time it is used.' })}
            required
          />
          <TextField
            label={t('expensesPage.fields.vendor')}
            fullWidth sx={{ mt:2 }}
            value={form.vendor}
            onChange={e=>setForm(f=>({...f,vendor:e.target.value}))}
            placeholder={t('expensesPage.placeholders.vendor')}
          />
          <TextField
            label={t('expensesPage.fields.incurredAt')}
            type="date"
            fullWidth sx={{ mt:2 }}
            value={form.incurredAt}
            InputLabelProps={{ shrink:true }}
            onChange={e=>setForm(f=>({...f,incurredAt:e.target.value}))}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDialogOpen(false)}>{t('actions.cancel')}</Button>
          <Button variant="contained" onClick={handleSave}>{t('actions.save')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={()=>setSnack("")}
        message={snack}
        action={
          <IconButton size="small" color="inherit" onClick={()=>setSnack("")}>
            <CloseIcon fontSize="small"/>
          </IconButton>
        }
      />
    </Box>
  );
}
